import { action, computed, observable } from "mobx";

import { DateTime } from "@bps/date-time";
import {
  AddClaimDto,
  ClaimDiagnosisDto,
  ClaimDto,
  ClaimProviderDto,
  ClaimStatuses,
  DiagnosisSides
} from "@libs/gateways/acc/AccGateway.dtos.ts";
import {
  DiagnosisDataItemDto,
  EpisodeOfCareDto,
  SideOfBody
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import {
  addNewClaim,
  convertToClaimPatient,
  patchExistingClaim,
  trimHpiCpn
} from "@modules/acc/screens/claim/components/utils.ts";
import { ConditionsSidePanelHelper } from "@modules/clinical/screens/patient-record/components/claims/ConditionsSidePanelHelper.ts";
import { IRootStore } from "@shared-types/root/root-store.interface.ts";
import { Claim } from "@stores/acc/models/Claim.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { EpisodeOfCare } from "@stores/clinical/models/EpisodeOfCare.ts";
import { Contact } from "@stores/practice/models/Contact.ts";

import { ConditionModalFormValues } from "../condition-modal/Condition.types.ts";

export class ConditionHelper {
  constructor(
    private root: IRootStore,
    private _patientId: string,
    private options: {
      episodeOfCare?: EpisodeOfCare;
      clinicalRecord?: ClinicalRecord;
      onSubmit: (
        values: ConditionModalFormValues,
        helper: ConditionHelper
      ) => Promise<void>;
      onSubmitSucceeded?: (episodeOfCare: EpisodeOfCare) => void;
      sidePanelHelper?: ConditionsSidePanelHelper;
    }
  ) {
    this.episodeOfCare = this.options.episodeOfCare;
    this.clinicalRecord = this.options.clinicalRecord;

    this._onSubmitSucceeded = this.options.onSubmitSucceeded;
  }

  private _onSubmitSucceeded;

  get acc() {
    return this.root.acc;
  }

  get userExperience() {
    return this.root.userExperience;
  }

  get clinical() {
    return this.root.clinical;
  }

  get practice() {
    return this.root.practice;
  }

  get notification() {
    return this.root.notification;
  }

  get patientId() {
    return this._patientId;
  }

  public episodeOfCare: EpisodeOfCare | undefined;
  public clinicalRecord: ClinicalRecord | undefined;

  public get claimHasInvalidTransitionStatus() {
    const invalidStatuses: string[] = [
      ClaimStatuses.Pending,
      ClaimStatuses.Held,
      ClaimStatuses.NotVerified,
      ClaimStatuses.Queued
    ];

    const hasInvalidStatus =
      !!this.episodeOfCare?.claim?.claimStatus &&
      invalidStatuses.includes(this.episodeOfCare.claim.claimStatus);

    return hasInvalidStatus;
  }

  public get claimHasBeenBilled() {
    const billed = this.episodeOfCare?.claim?.billed;
    const hasBeenBilled = !!billed && billed > 0;

    return hasBeenBilled;
  }

  @observable
  hiddenConditionModal: boolean = true;

  @observable
  isCalledFromNewCondition: boolean = false;

  showConditionModal = action((isCalledFromNewCondition: boolean) => {
    this.hiddenConditionModal = false;
    this.isCalledFromNewCondition = isCalledFromNewCondition;
  });

  hideConditionModal = action(() => {
    this.hiddenConditionModal = true;
  });

  patientCommunications = (patientId: string) => {
    return this.practice?.contactsMap?.get(patientId)?.communications;
  };

  submitConditionModal = async (values: ConditionModalFormValues) => {
    await this.options.onSubmit(values, this);
  };

  private convertClinicalSideToDiagnosisSide = (value: string | undefined) => {
    switch (value) {
      case SideOfBody.Left:
        return DiagnosisSides.Left;

      case SideOfBody.Right:
        return DiagnosisSides.Right;

      case SideOfBody.Both:
        return DiagnosisSides.Bilateral;

      default:
      case SideOfBody.Neither:
        return DiagnosisSides.NotApplicable;
    }
  };

  /**
   * Called upon submission of a new condition form
   * Creates an episode of care and claim (unless private).
   * If called as part of a consult (clinicalRecord is provided), it will also update medical history claim data.
   */
  submitAddConditionValues = async (
    values: ConditionModalFormValues,
    providerId?: string
  ) => {
    await this.createEpisodeOfCare(values);

    await Promise.all([
      this.createClaim(values, providerId),
      this.addEpisodeOfCareIdToMedicalHistory(values)
    ]);
  };

  /**
   * Creates and saves an episode of care
   * This is called during the submitAddConditionValues
   */
  private createEpisodeOfCare = async (values: ConditionModalFormValues) => {
    const addDto: Partial<EpisodeOfCareDto> = {
      patientId: this.patientId
    };

    const selectedDiagnoses = this.getEpisodeOfCareDiagnosis(values);

    if (selectedDiagnoses) {
      addDto.diagnoses = selectedDiagnoses;
    }

    if (values.referralIn) {
      addDto.referralNumber = values.referralNumber;
      addDto.referralProvider = values.referralProvider;
      addDto.referralDate = DateTime.jsDateToISODate(values.referralDate);
      addDto.isReferral = values.referralIn;
    }

    const episodeOfCare = await this.clinical.addEpisodeOfCare(addDto);

    this.episodeOfCare = episodeOfCare;
    return this.episodeOfCare;
  };

  /**
   * Returns a diagnosis data item dto, ready for saving to the episode of care
   * This is called during the submitAddConditionValues
   * */
  private getEpisodeOfCareDiagnosis = (
    values: ConditionModalFormValues
  ): DiagnosisDataItemDto[] | undefined => {
    if (
      !this.clinicalRecord ||
      !this.root.core.hasPermissions(Permission.AddDiagnosisAllowed)
    ) {
      return undefined;
    }

    const diagnosis = this.clinicalRecord.medicalHistories.find(
      x => x.diagnosisCode?.originalText === values?.diagnosis
    );

    return diagnosis
      ? [
          {
            diagnosisCode: diagnosis.diagnosisCode,
            diagnosisSide: diagnosis.diagnosisSide,
            isPrimaryDiagnosis: diagnosis.isPrimary // Should this always be true?
          }
        ]
      : [];
  };

  /**
   * Returns a condition, after creating a claim and claimEpisodeOfCare unless the condition is private
   * This is called during the submitAddConditionValues
   */
  private createClaim = async (
    values: ConditionModalFormValues,
    providerId: string | undefined
  ) => {
    if (!this.episodeOfCare || values.private) {
      return;
    }

    const claim = await this.addClaim(
      values,
      providerId ?? this.root.core.userId
    );

    claim.sendAccForm = values.sendAccForm;

    await this.acc.addClaimEpisodeOfCare(claim.id, this.episodeOfCare.id);
  };

  /**
   * Returns the primary diagnosis, for saving onto the claim
   * This is called during the submitAddConditionValues
   */
  private getClaimDiagnoses = (
    values: ConditionModalFormValues
  ): ClaimDiagnosisDto[] | undefined => {
    if (!values.diagnosisKey || !this.episodeOfCare?.diagnoses?.length) {
      return undefined;
    }

    const diagnosis = this.episodeOfCare.diagnoses.find(
      x => x.isPrimaryDiagnosis
    );

    const terminology = this.clinical.getTerminologyFromMap(
      values?.diagnosisKey
    );

    return [
      {
        diagnosisSide: this.convertClinicalSideToDiagnosisSide(
          diagnosis?.diagnosisSide
        ),
        terminology: {
          ...terminology,
          diagnosisKey: values?.diagnosisKey ?? "",
          code: diagnosis?.diagnosisCode?.code ?? "",
          text: diagnosis?.diagnosisCode?.originalText ?? ""
        }
      }
    ];
  };

  /**
   * Updates the medical history clinical data to include the episode of care ID.
   * This is called during the submitAddConditionValues and submitUpdateConditionValues
   */
  private addEpisodeOfCareIdToMedicalHistory = async (
    values: ConditionModalFormValues
  ) => {
    if (
      !this.episodeOfCare ||
      !this.clinicalRecord ||
      !values.diagnosis ||
      !this.root.core.hasPermissions(Permission.AddDiagnosisAllowed)
    ) {
      return;
    }

    if (
      this.clinicalRecord.medicalHistories.some(
        medicalHistory =>
          medicalHistory.episodeOfCareId === this.episodeOfCare?.id
      )
    ) {
      return;
    }

    let updateRequired = false;

    const updatedMedicalHistories = this.clinicalRecord.medicalHistories.map(
      medicalHistoryItem => {
        if (
          medicalHistoryItem.diagnosisCode?.originalText ===
            values?.diagnosis &&
          !medicalHistoryItem.episodeOfCareId
        ) {
          updateRequired = true;
          return {
            ...medicalHistoryItem,
            episodeOfCareId: this.episodeOfCare?.id
          };
        }
        return medicalHistoryItem;
      }
    );

    if (updateRequired) {
      await this.clinical.updateClinicalData(
        this.clinicalRecord.openEncounter?.id ?? "",
        {
          medicalHistory: {
            ...this.clinicalRecord.clinicalData?.medicalHistory,
            noSignificantHistory:
              !!this.clinicalRecord.clinicalData?.medicalHistory
                ?.noSignificantHistory,
            medicalHistories: updatedMedicalHistories
          }
        }
      );
    }
  };

  /**
   * Called upon submission of an update condition form
   * Creates an episode of care and claim (unless private).
   * If called as part of a consult (clinicalRecord is provided), it will also update medical history claim data.
   */
  submitUpdateConditionValues = async (
    values: ConditionModalFormValues,
    providerId?: string
  ) => {
    if (!this.episodeOfCare) return;

    await this.updateEpisodeOfCare(values);

    if (this.hasClaimWritePermission()) {
      await this.handlePrivacyChange(values, providerId);
      if (!values.private) {
        await this.updateClaim(values, providerId);
        await this.queueCheckClaimStatusIfClaimNumberChanged(values);
      }
    }

    if (this.episodeOfCare) {
      await this.addEpisodeOfCareIdToMedicalHistory(values);
    }
  };

  private hasClaimWritePermission(): boolean {
    return this.root.core.hasPermissions(Permission.ClaimWrite);
  }

  /**
   * If the user has changed whether the episode of care is private or public, send the required updates
   * This is called during the submitUpdateConditionValues
   */
  private handlePrivacyChange = async (
    values: ConditionModalFormValues,
    providerId?: string
  ) => {
    if (!this.episodeOfCare) return;
    if (!!values.private === !!this.episodeOfCare.isPrivate) return;

    if (values.private) {
      await this.changeToPrivate(this.episodeOfCare.claim?.id);
    } else {
      await this.changeToPublic(values, providerId);
    }
  };

  /**
   * Updates the claim to "private" and removes the claimAppointment and claimEpisodeOfCare
   * This is called during the submitUpdateConditionValues
   */
  private changeToPrivate = async (claimId?: string) => {
    if (!claimId) return;

    await this.acc.patchClaim({ id: claimId, private: true });
    await this.unlinkClaimFromEOC(claimId);
    await this.unlinkClaimAppointmentAndEncounter(claimId);
  };

  /**
   * When changing the condition to public, this method creates a claim, claimAppointment, and claimEpisodeOfCare
   * This is called during the submitUpdateConditionValues
   */
  private changeToPublic = async (
    values: ConditionModalFormValues,
    providerId?: string
  ) => {
    const claim = await this.addClaim(values, providerId);
    await this.addClaimEpisodeOfCareAndLinkToAppointments(claim.id);
    await this.linkClaimAppointment(claim.id); // this might not be needed
  };

  /**
   * Updates the episode of care with the new values
   * This is called during the submitUpdateConditionValues
   */
  private updateEpisodeOfCare = async (values: ConditionModalFormValues) => {
    if (!this.episodeOfCare) return;

    let diagnoses = this.episodeOfCare.diagnoses;

    if (values.diagnosis !== undefined) {
      const selectedDiagnoses = this.getEpisodeOfCareDiagnosis(values);
      if (selectedDiagnoses) {
        if (!diagnoses.some(x => x.isPrimaryDiagnosis)) {
          diagnoses = [...diagnoses, ...selectedDiagnoses];
        } else {
          diagnoses = selectedDiagnoses;
        }
      }
    }

    const dto: EpisodeOfCareDto = {
      ...this.episodeOfCare.dto,
      diagnoses,
      referralNumber: this.getReferralNumber(values),
      referralProvider: values.referralIn ? values.referralProvider : undefined,
      referralDate: values.referralIn
        ? DateTime.jsDateToISODate(values.referralDate)
        : undefined,
      isReferral: values.referralIn
    };

    await this.clinical.updateEpisodeOfCare(dto, this.episodeOfCare.id);
  };

  private getReferralNumber = (values: ConditionModalFormValues) => {
    if (values.referralIn) return values.referralNumber;
    if (
      this.episodeOfCare?.claim?.canUpdateClaimNumber &&
      values.referralNumber !== this.episodeOfCare.referralNumber
    ) {
      return values.referralNumber;
    }
    return undefined;
  };

  private unlinkClaimFromEOC = async (claimId: string) => {
    if (!this.episodeOfCare) return;

    const claimEpisodesOfCare = await this.acc.getClaimEpisodesOfCare({
      claimId,
      episodesOfCare: [this.episodeOfCare.id]
    });

    if (claimEpisodesOfCare.length) {
      const claimEOC = claimEpisodesOfCare[0];
      claimEOC.isActive = false;
      await this.acc.updateClaimEpisodeOfCare(claimEOC);
    }
  };

  private unlinkClaimAppointmentAndEncounter = async (claimId: string) => {
    if (!this.episodeOfCare) return;
    if (
      this.options.sidePanelHelper?.isLinkedEpisodeOfCare(this.episodeOfCare.id)
    ) {
      await this.options.sidePanelHelper?.unlinkClaimAppt(claimId);
    }
  };

  private linkClaimAppointment = async (claimId: string) => {
    if (!this.episodeOfCare) return;
    if (
      this.options.sidePanelHelper?.isLinkedEpisodeOfCare(this.episodeOfCare.id)
    ) {
      await this.options.sidePanelHelper?.linkClaimAppointment(claimId);
    }
  };

  addClaimEpisodeOfCareAndLinkToAppointments = async (claimId: string) => {
    if (!this.episodeOfCare) return;

    await this.acc.addClaimEpisodeOfCare(claimId, this.episodeOfCare.id);

    // Link any previously created private appointments
    if (this.episodeOfCare.calendarEvents?.length) {
      await Promise.all(
        this.episodeOfCare.calendarEvents.map(calendarEvent =>
          this.acc
            .getClaimAppointmentDtos({ calendarEventId: calendarEvent.id })
            .then(claimApps => {
              return !claimApps.length
                ? this.acc.addClaimAppointment({
                    claimId,
                    calendarEventId: calendarEvent.id
                  })
                : undefined;
            })
        )
      );
    }
  };

  /**
   * Creates part of the dto when adding a new claim
   * This is called during submitAddConditionValues and submitUpdateConditionValues
   */
  private getClaimDto = (
    values: ConditionModalFormValues,
    patient: Contact | undefined,
    providerId: string | undefined
  ): AddClaimDto => {
    const claimPatient = convertToClaimPatient({
      practice: this.practice,
      contact: patient
    });

    return {
      patientId: patient?.id,
      providerId,
      ...claimPatient,
      referralIn: values.referralIn ?? false,
      claimNumber: values.referralNumber?.toUpperCase(),
      statusCode:
        values.claimStatusCode ||
        (values.referralIn ? ClaimStatuses.NotVerified : undefined),
      insurerContactId: values.private ? undefined : values.insurerContactId,
      patientDateOfBirth: claimPatient
        ? DateTime.jsDateToISODate(claimPatient.patientDateOfBirth)
        : undefined,
      referralDate: DateTime.jsDateToISODate(values.referralDate)
    };
  };

  handleSubmitSucceeded = (values: ConditionModalFormValues) => {
    const typeDescription = values.private ? "private" : "claim";
    if (this.options.episodeOfCare)
      this.notification.success(
        `The ${typeDescription} condition has been updated`
      );
    else
      this.notification.success(
        `A new ${typeDescription} condition has been created`
      );

    this.hideConditionModal();

    if (this._onSubmitSucceeded && this.episodeOfCare) {
      this._onSubmitSucceeded(this.episodeOfCare);
    }
  };

  /**
   * This is called during the submitUpdateConditionValues
   */
  private async queueCheckClaimStatusIfClaimNumberChanged(
    values: ConditionModalFormValues
  ) {
    if (
      !values.private &&
      this.episodeOfCare &&
      values.referralNumber &&
      values.referralNumber !== this.episodeOfCare.referralNumber &&
      this.episodeOfCare?.claim?.canUpdateClaimNumber
    ) {
      const claimId = this.episodeOfCare.claim?.id;

      if (claimId) {
        await this.acc.queueCheckClaimStatus(claimId);
      }
    }
  }

  /**
   * Creates a new claim.
   * This is called during submitAddConditionValues (new condition) and submitUpdateConditionValues (changing an existing private condition to public)
   */
  private async addClaim(
    values: ConditionModalFormValues,
    providerId?: string
  ): Promise<Claim> {
    if (!this.episodeOfCare) {
      throw new Error("Episode of care not saved correctly");
    }

    const [patient, claimProviderDto, insurer, parentOrgUnit] =
      await Promise.all([
        this.root.practice.getContact(this.patientId),
        providerId
          ? await this.getClaimProvider(providerId, values.referralIn)
          : {},
        values.insurerContactId
          ? this.root.practice.getContact(values.insurerContactId)
          : undefined,
        this.root.core.location?.parentOrgUnit?.id
          ? this.root.practice.getOrgUnit(
              this.root.core.location.parentOrgUnit.id
            )
          : undefined
      ]);

    const orgUnitData = this.episodeOfCare?.claim?.getOrgUnitData();

    const claimDto = {
      ...this.getClaimDto(values, patient, providerId),
      ...claimProviderDto,
      ...orgUnitData,
      insurerName: insurer ? insurer.name : undefined,
      hpiOrganisationNumber:
        parentOrgUnit?.nzOrgUnitIdentifier?.hpiOrganisationId,
      hpiOrganisationName:
        parentOrgUnit?.nzOrgUnitIdentifier?.hpiOrganisationName,
      claimDiagnosis: this.getClaimDiagnoses(values)
    };

    return addNewClaim(claimDto, this.root.acc);
  }

  /**
   * Updates a claim.
   * This is called during submitUpdateConditionValues
   */
  private async updateClaim(
    values: ConditionModalFormValues,
    providerId?: string
  ): Promise<Claim> {
    const claim = this.episodeOfCare?.claim;

    if (!claim) {
      throw new Error("Claim not loaded properly");
    }

    const [claimProviderDto, insurer] = await Promise.all([
      providerId ? this.getClaimProvider(providerId, values.referralIn) : {},
      !values.private && values.insurerContactId
        ? await this.root.practice.getContact(values.insurerContactId)
        : undefined
    ]);

    const claimNumber =
      values.referralIn || claim.canUpdateClaimNumber
        ? values.referralNumber?.toUpperCase()
        : undefined;

    const updateClaimDto: ClaimDto = {
      private: values.private,
      patientId: this.patientId,
      referralProvider: values.referralIn ? values.referralProvider : undefined,
      ...claimProviderDto,
      id: claim.id ?? "",
      eTag: claim.eTag ?? "",
      claimNumber: claimNumber || claim.claimNumber,
      statusCode: values.claimStatusCode,
      referralIn: values.referralIn ?? claim.referralIn ?? false,
      providerId: providerId ? providerId : claim.providerId,
      insurerContactId: values.private ? undefined : values.insurerContactId,
      referralDate: values.referralIn
        ? DateTime.jsDateToISODate(values.referralDate)
        : undefined,
      insurerName: insurer ? insurer.name : undefined,
      errorMessages:
        values.claimStatusCode === ClaimStatuses.Accepted ||
        values.claimStatusCode === ClaimStatuses.Accredited
          ? undefined
          : claim.errorMessageList
    };

    const claimDiagnosis = this.getClaimDiagnoses(values);
    if (claimDiagnosis) {
      updateClaimDto.claimDiagnosis = claimDiagnosis;
    }

    return patchExistingClaim(updateClaimDto, this.root.acc).catch(async () => {
      // There is sometimes an issue with this request where the front end doesn't have the latest eTag for the claim
      await this.root.acc.getClaim(claim.id, { ignoreCache: true });
      updateClaimDto.eTag = claim.eTag;
      return patchExistingClaim(updateClaimDto, this.root.acc);
    });
  }

  /**
   * Gets the provider-related data needed for creating a claim or updating a claim.
   * This is called during submitAddConditionValues and submitUpdateConditionValues
   */
  private async getClaimProvider(
    providerId: string,
    referralIn?: boolean
  ): Promise<ClaimProviderDto> {
    const [user, provider] = await Promise.all([
      this.root.core.getUser(providerId),
      this.root.practice.getProvider(providerId)
    ]);

    const providerTypes = this.acc.getAvailableProviderTypes(
      provider?.contractTypes
    );

    return {
      providerFirstName: user.firstName,
      providerMiddleName: user.middleName,
      providerLastName: user.lastName,
      hpiCpn: !referralIn ? trimHpiCpn(provider?.cpn) : undefined, // max accepted hpi cpn length by ACC is 6
      providerTypeCode:
        providerTypes.length === 1 ? providerTypes[0].key : undefined
    };
  }

  @computed
  get initialValues(): ConditionModalFormValues {
    return {
      private:
        this.episodeOfCare?.isPrivate ??
        (!this.episodeOfCare &&
        this.root.core.hasPermissions(Permission.ClaimWrite)
          ? undefined
          : true),
      insurerContactId: this.episodeOfCare?.claim?.insurerContactId,
      insurerName: this.episodeOfCare?.claim?.insurerName,
      referralIn: this.episodeOfCare?.isReferral ?? false,
      referralNumber: this.episodeOfCare?.referralNumber,
      sendAccForm: this.episodeOfCare?.claim?.sendAccForm,
      referralProvider: this.episodeOfCare?.referralProvider,
      referralDate: this.episodeOfCare?.referralDate?.toJSDate(),
      claimStatusCode: this.episodeOfCare?.claim?.dto.statusCode,
      claimId: this.episodeOfCare?.claim?.dto.id,
      diagnosis: this.episodeOfCare?.primaryDiagnosis,
      diagnosisWithSide: this.episodeOfCare?.diagnosisSide
    };
  }

  public static getClaimBadgeText = (
    episodeOfCare: EpisodeOfCare | undefined
  ) => {
    if (episodeOfCare?.claim?.claimNumber) {
      return episodeOfCare?.claim?.claimNumber;
    }

    if (episodeOfCare && episodeOfCare.isPrivate) {
      return "Private";
    }

    return "Not lodged";
  };
}
